import {
  cmpCertificate,
  propertiesImg,
  propertymarkCertificate,
  prsCertificate,
  qualificationLogo,
  rentIllustration,
} from "../assets/assetsExport.tsx";

// GENERAL //
interface CompanyInfoType {
  email: string;
  phone: string;
  address: string;
}

export const companyInfo: CompanyInfoType = {
  email: "contact@lettingengine.com",
  phone: "+44 (0)20 3907 4025",
  address: "16 Berkeley Street; Mayfair, London; W1J 8DZ; UK",
};

// ALL HERO CONTEXT //
interface HeroContentType {
  h1: string;
  intro: string;
  learnMore?: string;
}

export const heroHome: HeroContentType = {
  h1: "Welcome to Letting Engine",
  intro:
    "Letting Engine is a premier residential property letting agency specialising in luxury rentals in London. Our mission is to provide a seamless and exceptional letting service, offering an exclusive selection of high-end properties in prime locations across the city. With our expertise and dedication, we aim to match discerning tenants with their ideal luxury home.",
};

export const heroLandlords: HeroContentType = {
  h1: "Letting Your Luxury Property",
  intro:
    "At Letting Engine, we understand that letting your luxury property requires a strategic approach to attract the right tenants and maximize your return on investment. With our comprehensive landlord services, we take care of every aspect of the letting process, ensuring a hassle-free experience for you.",
  learnMore:
    "Learn more about our landlord services and get started on letting your luxury property today.",
};

export const heroLetting: HeroContentType = {
  h1: "To Rent",
  intro:
    "At Letting Engine, we offer an exclusive selection of luxury properties in prime London locations. Whether you're looking for a stylish apartment, a lavish penthouse, or an elegant townhouse, our dedicated team is here to help you find your dream home.",
  learnMore: "Learn more about our letting services and featured properties.",
};

export const heroQualifications: HeroContentType = {
  h1: "Qualifications",
  intro:
    "At Letting Engine, we pride ourselves on our exceptional qualifications and commitment to regulatory excellence. As a renowned real estate company, we hold ourselves to the highest standards and diligently adhere to all regulations and industry best practices, to protect both landlords and tenants.",
  learnMore: "Learn more about our qualifications",
};

export const heroAboutUs: HeroContentType = {
  h1: "About Us",
  intro:
    "Letting Engine is a premier residential property letting agency based in London, specialising in luxury rentals. With a prime location in the heart of the city, we offer an extensive portfolio of high-end properties in sought-after neighborhoods. Our dedicated team of experts is committed to delivering exceptional service, providing a seamless letting experience for both landlords and tenants.",
};
export const heroContactUs: HeroContentType = {
  h1: "Contact Us",
  intro:
    "Ready to let your luxury property or find your dream rental in London? Get in touch with our team today to discuss your letting needs or schedule a property viewing.",
};

// HOME PAGE //
interface WhyUsItemType {
  header: string;
  p: string;
}

export const whyUs: WhyUsItemType[] = [
  {
    header: "Expertise and Experience",
    p: "With years of experience in the luxury property market, we have developed a deep understanding of London's rental landscape. Our knowledgeable team possesses the expertise to guide you through every step of the letting process, ensuring optimal results.",
  },
  {
    header: "Extensive Network",
    p: "Our extensive network of property owners, investors, and high-net-worth individuals allows us to access the most exclusive and desirable properties in London. We offer a curated selection of luxury homes that reflect the unique tastes and preferences of our discerning clientele.",
  },
  {
    header: "Personalized Service",
    p: "We prioritize personalized service and tailor our approach to meet your individual requirements. Our dedicated letting agents are committed to providing exceptional customer service, assisting you at every stage of the letting process and ensuring a smooth and hassle-free experience.",
  },
  {
    header: "Commitment to Quality",
    p: "We maintain uncompromising standards when it comes to the properties we represent. Our meticulous selection process ensures that only the finest residences, with exceptional design, outstanding amenities, and prime locations, are included in our portfolio.",
  },
];

// RENT PAGE //
interface RentingProcessItemType {
  header: string;
  p: string;
  img: { src: string; alt: string };
}

export const rentingProcess: RentingProcessItemType[] = [
  {
    header: "Property Search",
    p: "Browse our curated collection of high-end properties available for rent.",
    img: {
      src: rentIllustration.search,
      alt: "illustration related to search",
    },
  },
  {
    header: "Property Viewings",
    p: "Schedule property viewings at your convenience and let our experienced agents showcase the unique features and luxurious amenities of each residence.",
    img: {
      src: rentIllustration.viewing,
      alt: "illustration related to viewing",
    },
  },
  {
    header: "Application Process",
    p: "Once you've found your ideal property, we'll guide you through the application process and handle all necessary documentation, ensuring a smooth and efficient leasing experience.",
    img: {
      src: rentIllustration.application,
      alt: "illustration related to application",
    },
  },
  {
    header: "Ongoing Support",
    p: "Our dedicated team is available to assist you throughout your tenancy, providing prompt and reliable support for any inquiries or maintenance requests.",
    img: {
      src: rentIllustration.support,
      alt: "illustration related to support",
    },
  },
];

interface FeaturedPropertyInfoType {
  name: string;
  location: string;
  type: string;
  bedroom: string;
  bathroom: string;
  price: string;
}

interface FeaturedPropertyType {
  img: { src: string; alt: string };
  tag: string;
  info: FeaturedPropertyInfoType;
  link: string;
}

export const featuredProperties: FeaturedPropertyType[] = [
  {
    img: { src: propertiesImg.heron1208, alt: "The Heron flat" },
    tag: "",
    info: {
      name: "The Heron",
      location: "London, EC2Y",
      type: "Apartment",
      bedroom: "3",
      bathroom: "3",
      price: "£14,290",
    },
    link: "https://www.rightmove.co.uk/properties/168541301#/?channel=RES_LET",
  },
  {
    img: { src: propertiesImg.garraway1208, alt: "Garraway Apartments flat" },
    tag: "",
    info: {
      name: "Garraway Apartments, Western Circus",
      location: "London, W3",
      type: "Apartment",
      bedroom: "1",
      bathroom: "1",
      price: "£2,297",
    },
    link: "https://www.rightmove.co.uk/properties/168537881#/?channel=RES_LET",
  },
  {
    img: { src: propertiesImg.conquest1208, alt: "Conquest Tower flat" },
    tag: "",
    info: {
      name: "Conquest Tower",
      location: "London, SE1",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,199",
    },
    link: "https://www.rightmove.co.uk/properties/165377999#/?channel=RES_LET",
  },
  {
    img: { src: propertiesImg.warwick1208, alt: "Warwick Way flat" },
    tag: "",
    info: {
      name: "Warwick Way",
      location: "London, SW1V",
      type: "Apartment",
      bedroom: "2",
      bathroom: "1",
      price: "£3,000",
    },
    link: "https://www.rightmove.co.uk/properties/168945407#/?channel=STU_LET",
  },
  {
    img: { src: propertiesImg.john1006, alt: "John Cabot House flat" },
    tag: "",
    info: {
      name: "John Cabot House",
      location: "London, E16",
      type: "Apartment",
      bedroom: "1",
      bathroom: "1",
      price: "£1,999",
    },
    link: "https://www.rightmove.co.uk/properties/167282369#/?channel=RES_LET",
  },

  {
    img: { src: propertiesImg.truro0813, alt: "Truro House flat" },
    tag: "Let Agreed",
    info: {
      name: "Truro House",
      location: "London, WC1X",
      type: "Apartment",
      bedroom: "1",
      bathroom: "1",
      price: "£3,700",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.one0826, alt: "One Crown Place flat" },
    tag: "Let Agreed",
    info: {
      name: "One Crown Place",
      location: "London, EC2A",
      type: "Apartment",
      bedroom: "1",
      bathroom: "1",
      price: "£3,549",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.brunswick0826, alt: "Brunswick Quay flat" },
    tag: "Let Agreed",
    info: {
      name: "Brunswick Quay",
      location: "London, SE16",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£1,859",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.oval0826, alt: "Oval Village flat" },
    tag: "Let Agreed",
    info: {
      name: "Oval Village",
      location: "London, SE11",
      type: "Apartment",
      bedroom: "1",
      bathroom: "1",
      price: "£2,999",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.albright0826,
      alt: "Albright Gardens town house",
    },
    tag: "Let Agreed",
    info: {
      name: "Albright Gardens",
      location: "London, KT12",
      type: "Town House",
      bedroom: "3",
      bathroom: "3",
      price: "£3,500",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.embassy0813, alt: "Embassy Garden flat" },
    tag: "Let Agreed",
    info: {
      name: "Embassy Garden",
      location: "London, SW11",
      type: "Apartment",
      bedroom: "2",
      bathroom: "2",
      price: "£4,056",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.dumont0813, alt: "The Dumont flat" },
    tag: "Let Agreed",
    info: {
      name: "The Dumont",
      location: "London, SE1",
      type: "Apartment",
      bedroom: "2",
      bathroom: "2",
      price: "£4,550",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.oneThamesQuay0813, alt: "One Thames Quay flat" },
    tag: "Let Agreed",
    info: {
      name: "One Thames Quay",
      location: "London, E14",
      type: "Apartment",
      bedroom: "2",
      bathroom: "2",
      price: "£3,497",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.harcourtTower0813, alt: "Harcourt Tower flat" },
    tag: "Let Agreed",
    info: {
      name: "Harcourt Tower",
      location: "London, E14",
      type: "Apartment",
      bedroom: "2",
      bathroom: "1",
      price: "£3,497",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.embassy08132, alt: "Embassy Garden flat" },
    tag: "Let Agreed",
    info: {
      name: "Embassy Garden",
      location: "London, SW11",
      type: "Apartment",
      bedroom: "2",
      bathroom: "2",
      price: "£4,329",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.cheniston0210, alt: "Cheniston Garden tarraced" },
    tag: "Let Agreed",
    info: {
      name: "Cheniston Garden",
      location: "London, W8",
      type: "Terraced",
      bedroom: "6",
      bathroom: "6",
      price: "£24,999",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.signature0508, alt: "Signature House flat" },
    tag: "Let Agreed",
    info: {
      name: "Signature House, Jubilee Walk",
      location: "London, WC1X",
      type: "Apartment",
      bedroom: "1",
      bathroom: "1",
      price: "£3,400",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.londondock0508, alt: "London Dock flat" },
    tag: "Let Agreed",
    info: {
      name: "London Dock",
      location: "London, E1W 2AR",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£2,500",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.westernCircus0813, alt: "Western Circus flat" },
    tag: "Let Agreed",
    info: {
      name: "Western Circus",
      location: "London, W3",
      type: "Apartment",
      bedroom: "2",
      bathroom: "2",
      price: "£3,185",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.conquestTower0813, alt: "Conquest Tower flat" },
    tag: "Let Agreed",
    info: {
      name: "Conquest Tower",
      location: "London, SE1",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,199",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.parkside0508, alt: "Parkside Apartments flat" },
    tag: "Let Agreed",
    info: {
      name: "Parkside Apartments, Cascade Way",
      location: "London, W12",
      type: "Apartment",
      bedroom: "2",
      bathroom: "2",
      price: "£3,700",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.cluny0508, alt: "3 Cluny Mews flat" },
    tag: "Let Agreed",
    info: {
      name: "3 Cluny Mews",
      location: "London, SW5",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,400",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.dingley0508, alt: "417, Dingley Road flat" },
    tag: "Let Agreed",
    info: {
      name: "417, Dingley Road",
      location: "London, EC1V",
      type: "Apartment",
      bedroom: "1",
      bathroom: "1",
      price: "£3,300",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.gasholder0403, alt: "Gasholder Place flat" },
    tag: "Let Agreed",
    info: {
      name: "Gasholder Place",
      location: "London, SE11",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£2,850",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.oval02103800, alt: "Oval Village flat" },
    tag: "Let Agreed",
    info: {
      name: "Oval Village",
      location: "London, SE11",
      type: "Apartment",
      bedroom: "2",
      bathroom: "2",
      price: "£3,900",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.oval02102800, alt: "Oval Village flat" },
    tag: "Let Agreed",
    info: {
      name: "Oval Village",
      location: "London, SE11",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£2,800",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.fairbourne0210, alt: "Fairbourne Rd flat" },
    tag: "Let Agreed",
    info: {
      name: "Fairbourne Rd",
      location: "London, SW4",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£2,652",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.cluny0210, alt: "Cluny Mews flat" },
    tag: "Let Agreed",
    info: {
      name: "Cluny Mews",
      location: "London, SW5",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£4,500",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.onezero0210, alt: "On Cleveland flat" },
    tag: "Let Agreed",
    info: {
      name: "On Cleveland",
      location: "London, W1T",
      type: "Apartment",
      bedroom: "3",
      bathroom: "3",
      price: "£7,500",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.kidbrooke0210, alt: "Kidbrooke Village flat" },
    tag: "Let Agreed",
    info: {
      name: "Kidbrooke Village",
      location: "London, SE3",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£2,275",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.rw90jch, alt: "John Cabot House flat" },
    tag: "Let Agreed",
    info: {
      name: "John Cabot House",
      location: "London, E16",
      type: "Apartment",
      bedroom: "1",
      bathroom: "1",
      price: "£1,999",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.union0210, alt: "Union Wharf flat" },
    tag: "Let Agreed",
    info: {
      name: "Union Wharf",
      location: "London, UB7",
      type: "Apartment",
      bedroom: "2",
      bathroom: "1",
      price: "£1,750",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.edenplace0403, alt: "1303, Eden Place flat" },
    tag: "Let Agreed",
    info: {
      name: "1303, Eden Place",
      location: "London, E16",
      type: "Apartment",
      bedroom: "1",
      bathroom: "1",
      price: "£2,093",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.watkin0224, alt: "watkin road flat" },
    tag: "Let Agreed",
    info: {
      name: "Watkin Road",
      location: "London, HA9",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£1,885",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.watkin0210, alt: "Watkin Road flat" },
    tag: "Let Agreed",
    info: {
      name: "Watkin Road",
      location: "London, HA9",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£1,898",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.albany0210, alt: "Albany House flat" },
    tag: "Let Agreed",
    info: {
      name: "Albany House",
      location: "London, WC1H",
      type: "Studio",
      bedroom: "0",
      bathroom: "1",
      price: "£1,950",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.rw90ih, alt: "India House flat" },
    tag: "Let Agreed",
    info: {
      name: "India House",
      location: "London, E16",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£2,399",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.lincolnsquare6656, alt: "Lincoln Square flat" },
    tag: "Let Agreed",
    info: {
      name: "Lincoln Square",
      location: "London, WC2A",
      type: "Apartment",
      bedroom: "2",
      bathroom: "2",
      price: "£6,500",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.harcourt, alt: "Harcourt Tower flat" },
    tag: "Let Agreed",
    info: {
      name: "Harcourt Tower",
      location: "London, E14",
      type: "Flat",
      bedroom: "2",
      bathroom: "1",
      price: "£3,467",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.opus0224, alt: "32 Opus House flat" },
    tag: "Let Agreed",
    info: {
      name: "32 Opus House",
      location: "London, WC1X",
      type: "Apartment",
      bedroom: "2",
      bathroom: "2",
      price: "£4,200",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.damac0210, alt: "Damac Tower flat" },
    tag: "Let Agreed",
    info: {
      name: "Damac Tower",
      location: "London, SW8",
      type: "Apartment",
      bedroom: "3",
      bathroom: "3",
      price: "£9,966",
    },
    link: "",
  },

  {
    img: {
      src: propertiesImg.kidbrookevillageblackheath,
      alt: "Kidbrooke Village Blackheath flat",
    },
    tag: "Let Agreed",
    info: {
      name: "Kidbrooke Village Blackheath",
      location: "London, SE3",
      type: "Flat",
      bedroom: "4",
      bathroom: "3",
      price: "£3,185",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.ovalvillage2900, alt: "Oval Village flat" },
    tag: "Let Agreed",
    info: {
      name: "Oval Village",
      location: "London, SE11",
      type: "Apartment",
      bedroom: "1",
      bathroom: "1",
      price: "£2,900",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.parkdrive3042, alt: "10 Park Drive flat" },
    tag: "Let Agreed",
    info: {
      name: "10 Park Drive",
      location: "London, E14",
      type: "Apartment",
      bedroom: "1",
      bathroom: "1",
      price: "£3,042",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.woodberrydown1650, alt: "Woodberry Down flat" },
    tag: "Let Agreed",
    info: {
      name: "Woodberry Down",
      location: "London, Anax Street",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£1,650",
    },
    link: "",
  },

  ////////////////////////////////

  {
    img: { src: propertiesImg.empire, alt: "805, Empire House flat" },
    tag: "Let Agreed",
    info: {
      name: "805, Empire House",
      location: "London, W4",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£3,700",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.blackfriars3000, alt: "Blackfriars Road flat" },
    tag: "Let Agreed",
    info: {
      name: "Blackfriars Road",
      location: "London, SE1",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,000",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.darter2350, alt: "Darter Apartments flat" },
    tag: "Let Agreed",
    info: {
      name: "Darter Apartments",
      location: "London, N4",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£2,350",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.handley, alt: "Handley Drive flat" },
    tag: "Let Agreed",
    info: {
      name: "Handley Drive",
      location: "London, SE3",
      type: "Flat",
      bedroom: "4",
      bathroom: "3",
      price: "£3,458",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.hartingtons, alt: "Hartingtons Court flat" },
    tag: "Let Agreed",
    info: {
      name: "Hartingtons Court",
      location: "London, N4",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£2,500",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.oncleveland, alt: "On Cleveland flat" },
    tag: "Let Agreed",
    info: {
      name: "On Cleveland",
      location: "London, W1T",
      type: "Apartment",
      bedroom: "3",
      bathroom: "2",
      price: "£7,930",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.conquest, alt: "Conquest Tower flat" },
    tag: "Let Agreed",
    info: {
      name: "Conquest Tower, Blackfriars Road",
      location: "London, SE1",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,237",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.onetheelephant,
      alt: "One The Elephant, St. Gabriel Walk flat",
    },
    tag: "Let Agreed",
    info: {
      name: "One The Elephant, St. Gabriel Walk",
      location: "London, SE1",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,094",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.darter, alt: "Darter Apartments flat" },
    tag: "Let Agreed",
    info: {
      name: "Darter Apartments",
      location: "London, N4",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£2,500",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.viaductgardens4333, alt: "Dingley Road flat" },
    tag: "Let Agreed",
    info: {
      name: "Viaduct Gardens",
      location: "London, SW11",
      type: "Apartment",
      bedroom: "2",
      bathroom: "2",
      price: "£4,225",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.paragonsquare, alt: "Paragon Square flat" },
    tag: "Let Agreed",
    info: {
      name: "Paragon Square",
      location: "London, WC1X",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£5,000",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.hemlock,
      alt: "Hemlock Street flat",
    },
    tag: "Let Agreed",
    info: {
      name: "Hemlock Street",
      location: "London, E14",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£2,500",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.millbank60002,
      alt: "Millbank flat",
    },
    tag: "Let Agreed",
    info: {
      name: "Millbank",
      location: "London, SW1P",
      type: "Apartment",
      bedroom: "2",
      bathroom: "2",
      price: "£6,000",
    },
    link: "",
  },

  {
    img: {
      src: propertiesImg.jubilee3500,
      alt: "Jubilee Walk flat",
    },
    tag: "Let Agreed",
    info: {
      name: "Jubilee Walk",
      location: "London, WC1X",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,500",
    },
    link: "",
  },

  {
    img: {
      src: propertiesImg.clunymews4500,
      alt: "3 Cluny Mews flat",
    },
    tag: "Let Agreed",
    info: {
      name: "3 Cluny Mews",
      location: "London, SW5 9EG",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£4,500",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.clunymews3500,
      alt: "3 Cluny Mews flat",
    },
    tag: "Let Agreed",
    info: {
      name: "3 Cluny Mews",
      location: "London, SW5",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,500",
    },
    link: "",
  },

  {
    img: {
      src: propertiesImg.panpeninsula,
      alt: "Pan Peninsula Square flat",
    },
    tag: "Let Agreed",
    info: {
      name: "Pan Peninsula Square",
      location: "London, E14",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£4,030",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.viaductgardens4500, alt: "Viaduct Gardens flat" },
    tag: "Let Agreed",
    info: {
      name: "Viaduct Gardens",
      location: "London, SW11",
      type: "Apartment",
      bedroom: "2",
      bathroom: "2",
      price: "£4,500",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.marshwall, alt: "Marsh Wall flat" },
    tag: "Let Agreed",
    info: {
      name: "Marsh Wall",
      location: "London, E14",
      type: "Flat",
      bedroom: "2",
      bathroom: "1",
      price: "£3,500",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.jubileewalk3300, alt: "Jubilee Walk flat" },
    tag: "Let Agreed",
    info: {
      name: "Jubilee Walk",
      location: "London, WC1X",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,300",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.jubileewalk3500, alt: "Jubilee Walk flat" },
    tag: "Let Agreed",
    info: {
      name: "Jubilee Walk",
      location: "London, WC1X",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,500",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.mountpleasant, alt: "8 Mount Pleasant flat" },
    tag: "Let Agreed",
    info: {
      name: "8 Mount Pleasant",
      location: "London, WC1X 0BU",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£4,500",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.paragonsquare5000, alt: "Paragon Square flat" },
    tag: "Let Agreed",
    info: {
      name: "Paragon Square",
      location: "London, WC1X",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£5,000",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.coster, alt: "Coster Avenue flat" },
    tag: "Let Agreed",
    info: {
      name: "Coster Avenue",
      location: "London, N4",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£3,163",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.sunstreet, alt: "Sun Street flat" },
    tag: "Let Agreed",
    info: {
      name: "Sun Street",
      location: "London, EC2A",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,683",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.dingley, alt: "Dingley Road flat" },
    tag: "Let Agreed",
    info: {
      name: "Dingley Road",
      location: "London, EC1V",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,200",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.basil, alt: "a picture of basil street flat" },
    tag: "Let Agreed",
    info: {
      name: "Basil Street",
      location: "London, SW3",
      type: "Flat",
      bedroom: "4",
      bathroom: "5",
      price: "£16,000",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.parkside,
      alt: "a picture of Parkside Apartments",
    },
    tag: "Let Agreed",
    info: {
      name: "Parkside Apartments",
      location: "London, W12",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£2,600",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.clunymews3200, alt: "a picture of Cluny Mews" },
    tag: "Let Agreed",
    info: {
      name: "Cluny Mews",
      location: "London, SW5 9AG",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,100",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.jubilee3300, alt: "a picture of Jubilee Walk" },
    tag: "Let Agreed",
    info: {
      name: "Jubilee Walk",
      location: "London, WC1X",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,300",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.jubilee3000, alt: "a picture of Jubilee Walk" },
    tag: "Let Agreed",
    info: {
      name: "Jubilee Walk",
      location: "London, WC1X",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,000",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.salutation,
      alt: "a picture of Salutation Gardens",
    },
    tag: "Let Agreed",
    info: {
      name: "Salutation Gardens",
      location: "London, WC1X",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£4,200",
    },
    link: "",
  },

  // {
  //   img: { src: propertiesImg.copse, alt: "a picture of 39 Copse Hill" },
  //   tag: "",
  //   info: {
  //     name: "39 Copse Hill",
  //     location: "London, Wimbledon, SW20 0NJ",
  //     type: "Detached",
  //     bedroom: "5",
  //     bathroom: "6",
  //     price: "£16,000",
  //   },
  //   link: "https://www.rightmove.co.uk/properties/140603696#/?channel=RES_LET"
  // },

  {
    img: {
      src: propertiesImg.studiowhitecity,
      alt: "a picture of Studio, White City Living",
    },
    tag: "Let Agreed",
    info: {
      name: "Studio, White City Living",
      location: "London, W12 7RD",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£2,800",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.allium1730,
      alt: "a picture of Allium House apartment",
    },
    tag: "Let Agreed",
    info: {
      name: "Allium House, 2 Caldon Boulevard",
      location: "London, Wembley, HA0 1BD",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£1,730",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.legacy, alt: "a picture of Legacy Building" },
    tag: "Let Agreed",
    info: {
      name: "Legacy Building",
      location: "Viaduct Gardens, London, SW11",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£4,500",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.bentinck, alt: "a picture of Bentinck Road" },
    tag: "Let Agreed",
    info: {
      name: "Bentinck Road",
      location: "UB7",
      type: "Apartment",
      bedroom: "2",
      bathroom: "1",
      price: "£1,790",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.lincoln, alt: "a picture of Lincoln Apartments" },
    tag: "Let Agreed",
    info: {
      name: "Lincoln Apartments",
      location: "White City Living, London, W12",
      type: "Duplex",
      bedroom: "3",
      bathroom: "4",
      price: "£7,998",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.thenova, alt: "a picture of The Nova Building" },
    tag: "Let Agreed",
    info: {
      name: "The Nova Building",
      location: "London, SW1W 0AJ",
      type: "Flat",
      bedroom: "3",
      bathroom: "2",
      price: "£6,498",
    },
    link: "",
  },

  {
    img: {
      src: propertiesImg.blackfriars3200,
      alt: "a picture of Blackfriars Road",
    },
    tag: "Let Agreed",
    info: {
      name: "Blackfriars Road",
      location: "London, SE1",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,200",
    },
    link: "",
  },
  {
    img: { src: propertiesImg.clunymews4200, alt: "a picture of 3 Cluny Mews" },
    tag: "Let Agreed",
    info: {
      name: "3 Cluny Mews",
      location: "London, SW5 9EG",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£4,200",
    },
    link: "",
  },

  {
    img: {
      src: propertiesImg.allium,
      alt: "a picture of Allium House apartment",
    },
    tag: "Let Agreed",
    info: {
      name: "Allium House, 8 Belgrave Road",
      location: "London, Wembley",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£1,800",
    },
    link: "",
  },

  {
    img: { src: propertiesImg.parrs, alt: "a picture of Parrs Way apartment" },
    tag: "Let Agreed",
    info: {
      name: "Parrs Way",
      location: "London, W6",
      type: "Apartment",
      bedroom: "1",
      bathroom: "1",
      price: "£2,950",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.blackfriars,
      alt: "a picture of Blackfriars Road apartment",
    },
    tag: "Let Agreed",
    info: {
      name: "Blackfriars Road",
      location: "London, SE1",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,400",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.markville,
      alt: "a picture of Markville Gardens apartment",
    },
    tag: "Let Agreed",
    info: {
      name: "Markville Gardens, Caterham",
      location: "Surrey, CR3",
      type: "Detached",
      bedroom: "4",
      bathroom: "3",
      price: "£3,950",
    },
    link: "",
  },

  {
    img: {
      src: propertiesImg.one3700,
      alt: "a picture of One Cluny Mews apartment",
    },
    tag: "Let Agreed",
    info: {
      name: "One Cluny Mews",
      location: "London, SW5 9EG",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,700",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.one4500,
      alt: "a picture of One Cluny Mews apartment",
    },
    tag: "Let Agreed",
    info: {
      name: "One Cluny Mews",
      location: "London, SW5 9EG",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£4,500",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.one4200,
      alt: "a picture of One Cluny Mews apartment",
    },
    tag: "Let Agreed",
    info: {
      name: "One Cluny Mews",
      location: "London, SW5 9EG",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£4,200",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.one3300,
      alt: "a picture of One Cluny Mews apartment",
    },
    tag: "Let Agreed",
    info: {
      name: "One Cluny Mews",
      location: "London, SW5 9EG",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,000",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.moor15000,
      alt: "a picture of Moor Lane apartment",
    },
    tag: "Let Agreed",
    info: {
      name: "Moor Lane",
      location: "London, EC2Y",
      type: "Flat",
      bedroom: "3",
      bathroom: "3",
      price: "£15,000",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.moor17117,
      alt: "a picture of Moor Lane apartment",
    },
    tag: "Let Agreed",
    info: {
      name: "Moor Lane",
      location: "London, EC2Y",
      type: "Flat",
      bedroom: "3",
      bathroom: "3",
      price: "£17,117",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.millbank6000,
      alt: "a picture of 9 Millbank apartment",
    },
    tag: "Let Agreed",
    info: {
      name: "9 Millbank",
      location: "London SWIP",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£6,000",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.millbank6200,
      alt: "a picture of 9 Millbank apartment",
    },
    tag: "Let Agreed",
    info: {
      name: "9 Millbank",
      location: "London, SW1P",
      type: "Flat",
      bedroom: "2",
      bathroom: "2",
      price: "£6,200",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.blenheim,
      alt: "a picture of Blenheim Mansions apartment",
    },
    tag: "Let Agreed",
    info: {
      name: "Blenheim Mansions, 3 Mary Neuner Road",
      location: "London, N8",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£1,750",
    },
    link: "",
  },
  {
    img: {
      src: propertiesImg.mount,
      alt: "a picture of 8 Mount Pleasant apartment",
    },
    tag: "Let Agreed",
    info: {
      name: "8 Mount Pleasant",
      location: "London, WC1X 0BU",
      type: "Flat",
      bedroom: "1",
      bathroom: "1",
      price: "£3,400",
    },
    link: "",
  },
];

export const managedBuildingNames: string[] = Array.from(
  new Set(
    featuredProperties.map((property) =>
      property.info.name.split(",")[0].trim()
    )
  )
).sort((a, b) => a.localeCompare(b));

// LANDLORDS PAGE //
export const preLetting: any = [
  {
    header: "Initial Consultation:",
    p: "Conducting an initial consultation with landlords to understand their specific needs, requirements, and expectations.",
  },
  {
    header: "Property Evaluation: ",
    p: "Our experienced team will conduct a thorough evaluation of your property to determine its rental potential and advise on any enhancements that can increase its desirability.",
  },
  {
    header: "Marketing and Advertising: ",
    p: "Creating compelling property listings with professional photography and descriptions to attract a wide pool of qualified tenants. Utilizing various marketing channels, such as Rightmove, Zoopla, Red and WeChat, to maximize exposure.",
  },
  {
    header: "Targeted Marketing: ",
    p: "Leveraging our extensive network and innovative marketing strategies, we'll promote your property to qualified tenants, ensuring maximum exposure and minimizing vacancies.",
  },
  {
    header: "Tenant Screening and Selection: ",
    p: "We conduct a rigorous tenant screening process, including comprehensive background checks, credit checks, employment verification, financial assessments, and references to ensure that responsible and trustworthy tenants are selected.",
  },
  {
    header: "Contract Negotiation: ",
    p: "Our skilled negotiators handle tenancy contract negotiations on your behalf, ensuring favourable terms and conditions while protecting your interests.",
  },
  {
    header: "Tenancy Agreement and Documentation: ",
    p: "Preparing and facilitating the signing of legally compliant lease agreements and tenancy documentation, ensuring that all necessary terms and conditions are clearly outlined and agreed upon.",
  },
  {
    header: "Legal Compliance: ",
    p: "Staying up-to-date with relevant landlord and tenant legislation, regulations, and legal requirements to ensure full compliance. Providing guidance to landlords on their legal responsibilities and obligations.",
  },
];

export const duringLetting: any = [
  {
    header: "Check-in and Check-out Inventory: ",
    p: "Providing comprehensive inventory services to landlords, ensuring accurate documentation of the property's condition at the start and end of a tenancy. The detailed inventory report provides a clear record of the property's condition, helping to avoid disputes between landlords and tenants regarding deposit deductions for damages.",
  },
  {
    header: "Property Maintenance and Repairs:",
    p: "Coordinating and overseeing property maintenance, repairs, and inspections as needed. Responding promptly to tenant maintenance requests and ensuring that the property is well-maintained throughout the tenancy.",
  },
  {
    header: "Cleaning Services:",
    p: "To ensure that the property is presented in its best condition for new tenants, we arrange professional cleaning services to create a welcoming environment for tenants and to maintain the property's overall hygiene.",
  },
  {
    header: "Rent Collection and Financial Management: ",
    p: "Managing the collection and processing of rent payments, ensuring timely and accurate accounting. Providing landlords with detailed financial statements and rent payment reports.",
  },
  {
    header: "Tax Consultation: ",
    p: "Offering comprehensive tax consultation services to ensure that landlords understand their tax obligations, optimize their tax position, and maintain compliance with UK tax laws, thereby maximizing financial outcomes while minimizing any potential tax risks or issues. Providing guidance on the Non-Resident Landlord Scheme for overseas landlords.",
  },
  {
    header: "Tenant Relations: ",
    p: "Facilitating effective communication between landlords and tenants, addressing any concerns or issues that may arise during the tenancy.",
  },
  {
    header: "Communication and Support: ",
    p: "Maintaining open and transparent communication with landlords, providing regular updates on property performance, market trends, and any relevant information. Offering ongoing support and assistance throughout the letting process.",
  },
];

// ABOUT PAGE //
export const vision: string =
  "Our vision is to be the leading provider of luxury residential property letting services in London. We strive to set the highest standard in the industry, recognized for our exceptional properties, unparalleled customer service, and innovative approach. Our aim is to exceed the expectations of our clients, build long-lasting relationships, and be the go-to agency for luxury rentals in the city.";

export const mission: string =
  "Our mission is to connect discerning tenants with their perfect luxury homes while providing comprehensive services that meet the needs of landlords. We are committed to delivering a seamless letting process, offering an exclusive collection of high-end properties, and ensuring utmost satisfaction for both landlords and tenants. With our expertise, dedication, and personalized approach, we aim to make the letting experience exceptional and memorable.";

interface ValueItemType {
  header: string;
  p: string;
}

export const values: ValueItemType[] = [
  {
    header: "Excellence: ",
    p: "We strive for excellence in everything we do, setting the highest standards for service, property selection, and client satisfaction.",
  },
  {
    header: "Integrity: ",
    p: "We conduct our business with utmost integrity, maintaining transparency, honesty, and ethical practices in all our interactions.",
  },
  {
    header: "Customer-Centric: ",
    p: "Our clients are at the heart of everything we do. We prioritize their needs, preferences, and satisfaction, delivering personalized and exceptional service at every step.",
  },
  {
    header: "Expertise: ",
    p: "With our deep knowledge of the London property market and industry expertise, we provide informed guidance, market insights, and valuable advice to our clients.",
  },
  {
    header: "Innovation: ",
    p: "We embrace innovation and stay ahead of industry trends and technologies to continuously enhance our services, improve efficiency, and deliver an exceptional letting experience.",
  },
  {
    header: "Collaboration: ",
    p: "We foster strong partnerships and collaborations with landlords, tenants, and industry professionals, working together to achieve mutual success and satisfaction.",
  },
  {
    header: "Trust:",
    p: "Trust is the foundation of our relationships. We aim to build trust through open communication, reliability, and delivering on our promises.",
  },
];

export const qualifications: any[] = [
  {
    name: "propertymark",
    img: { src: qualificationLogo.propertymark, alt: "propertymark logo" },
    header: "Propertymark",
    description: [
      "<p>Letting Engine is a proud member of Propertymark, the leading professional body for property agents. Membership demonstrates our commitment to maintaining the highest industry standards and ensuring our clients receive expert, reliable service.</p>",
      "<p>Propertymark regulates and supports letting agents through strict compliance, ongoing training, and a professional code of conduct. Our affiliation with Propertymark reflects our dedication to professionalism, accountability, and providing complete peace of mind to landlords and tenants alike.</p>",
    ],
    link: {
      text: "download the Propertymark Certificate (PDF)",
      url: propertymarkCertificate,
    },
    code: "REG_01",
    badge: "Accredited",
    logo: qualificationLogo.propertymark,
    details: [
      { label: "Company Number", value: "C0135548" },
      { label: "Membership", value: "Active" },
    ],
  },
  {
    name: "cmp",
    img: { src: qualificationLogo.cmp, alt: "CMP logo" },
    header: "Client Money Protection (CMP)",
    description: [
      '<p>Letting Engine is pleased to announce our membership in the National Federation of Property Professionals Client Money Protection (CMP) Scheme through <a href="https://www.propertymark.co.uk/professional-standards/rules/cmp.html" target="_blank">Propertymark</a>.</p>',
      "<p>This membership highlights our commitment to providing a secure and trustworthy environment for our clients. The CMP Scheme ensures that any client funds entrusted to Letting Engine are handled with utmost responsibility and in compliance with industry regulations. By being a part of the CMP Scheme, we prioritize the protection of client funds and uphold the highest standards of professionalism and transparency in our operations. Clients can have peace of mind knowing that their money is safeguarded under the guidelines of the CMP Scheme when working with Letting Engine.</p>",
      "<p>Propertymark states: CMP is a scheme that reimburses landlords and tenants should a letting agent misappropriate their rent, deposit or other client funds. It has long been a requirement for ARLA Propertymark members to have CMP and has been a legal requirement for all letting agents since 1 April 2019.</p>",
      "<p>Accounting rules of CMP provider is available at: <a href='http://www.propertymark.co.uk/professional-standards/rules.html#obligations' target='_blank'>http://www.propertymark.co.uk/professional-standards/rules.html#obligations</a></p>",
    ],
    link: { text: "Download CMP Certificate (PDF)", url: cmpCertificate },
    code: "REG_02",
    badge: "Protected",
    logo: qualificationLogo.cmp,
    details: [
      { label: "Company Number", value: "C0135548" },
      { label: "Status", value: "Verified" },
    ],
  },
  {
    name: "prs",
    img: { src: qualificationLogo.prs, alt: "prs logo" },
    header: "Property Redress Scheme (PRS)",
    description: [
      "<p>Letting Engine is a proud member of the Property Redress Scheme (PRS). As a member, we adhere to the highest standards of professionalism and ethical practices in the property letting industry.</p>",
      "<p>The Property Redress Scheme provides an independent redress mechanism for landlords and tenants, ensuring that any disputes or complaints are handled fairly and efficiently. Our membership with PRS reflects our commitment to resolving any issues transparently and in accordance with industry regulations. We strive to provide our clients with peace of mind, knowing that they are supported by a reputable and accountable letting agency.</p>",
    ],
    link: { text: "download the PRS Certificate (PDF)", url: prsCertificate },
    code: "REG_03",
    badge: "Member",
    logo: qualificationLogo.prs,
    details: [
      { label: "Registration", value: "Active" },
      { label: "Redress", value: "Compliant" },
    ],
  },
  {
    name: "dps",
    img: { src: qualificationLogo.dps, alt: "DPS logo" },
    header: "Deposit Protection Service (DPS)",
    description: [
      "<p>Letting Engine is a registered member of the Deposit Protection Service (DPS). This ensures that all tenancy deposits we handle are protected in a government-approved scheme, providing security and compliance with legal requirements.</p>",
      "<p>The Deposit Protection Service safeguards tenants’ deposits and offers a fair resolution service in the event of disputes between landlords and tenants. Our registration with DPS demonstrates our dedication to transparency, accountability, and the protection of our clients’ interests at every stage of the letting process.</p>",
    ],
    code: "REG_04",
    badge: "Protected",
    logo: qualificationLogo.dps,
    details: [
      { label: "Registration", value: "Active" },
      { label: "Scheme", value: "Custodial" },
    ],
  },
];
